import React, { useState } from 'react';
import axios from 'axios';
import { 
    Box, Typography, TextField, Button, Paper, Container, 
    Snackbar, Alert, CircularProgress, InputAdornment, Avatar 
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';

// API URL
const API_URL = process.env.REACT_APP_API_URL;

const EditorBasvuruFormu = () => {
    // State Yönetimi
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        reason: ''
    });
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Input Değişikliği
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Form Gönderimi
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await axios.post(`${API_URL}/apply-editor`, formData);
            
            setSnackbar({
                open: true,
                message: 'Başvurunuz başarıyla alındı! En kısa sürede size dönüş yapacağız.',
                severity: 'success'
            });
            
            setFormData({ full_name: '', email: '', reason: '' });

        } catch (error) {
            console.error(error);
            setSnackbar({
                open: true,
                message: error.response?.data?.detail || 'Bir hata oluştu, lütfen tekrar deneyin.',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // --- Modern Dark Tema Stilleri ---
    
    // Input Alanları için Özel Stil (Koyu Zemin Uyumlu)
    const darkInputStyles = {
        mb: 3,
        '& .MuiOutlinedInput-root': {
            color: '#f3f4f6', // Yazı rengi (açık gri)
            backgroundColor: 'rgba(17, 24, 39, 0.5)', // Hafif transparan koyu zemin
            transition: 'all 0.3s ease',
            '& fieldset': { 
                borderColor: '#4b5563', // Kenarlık (gri)
                borderWidth: '1px'
            },
            '&:hover fieldset': { 
                borderColor: '#fbbf24' // Hover'da amber rengi
            },
            '&.Mui-focused fieldset': { 
                borderColor: '#f59e0b', // Odaklanınca koyu amber
                borderWidth: '2px'
            },
            '&:hover': {
                backgroundColor: 'rgba(17, 24, 39, 0.8)'
            }
        },
        '& .MuiInputLabel-root': { 
            color: '#9ca3af' // Label rengi (gri)
        },
        '& .MuiInputLabel-root.Mui-focused': { 
            color: '#fbbf24' // Odaklanınca label rengi
        }
    };

    return (
        <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-4">
            <Container maxWidth="sm">
                <Paper 
                    elevation={24}
                    sx={{
                        backgroundColor: '#1f2937', // Koyu gri (Gray-800)
                        color: 'white',
                        border: '1px solid #374151', // İnce gri çerçeve
                        borderRadius: 4,
                        p: { xs: 3, md: 5 }, // Mobilde daha az, masaüstünde daha çok padding
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    {/* Üst Dekoratif Çizgi (Gradient) */}
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '6px',
                        background: 'linear-gradient(90deg, #b45309 0%, #fbbf24 50%, #b45309 100%)'
                    }} />

                    {/* Başlık ve İkon Alanı */}
                    <Box className="text-center mb-8">
                        <Avatar sx={{ 
                            width: 70, 
                            height: 70, 
                            bgcolor: 'rgba(251, 191, 36, 0.1)', 
                            color: '#fbbf24',
                            margin: '0 auto',
                            mb: 2
                        }}>
                            <EditIcon sx={{ fontSize: 35 }} />
                        </Avatar>
                        
                        <Typography variant="h4" sx={{ 
                            fontWeight: 700, 
                            background: 'linear-gradient(to right, #fcd34d, #f59e0b)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1
                        }}>
                            Editörlük Başvurusu
                        </Typography>
                        
                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                            Mekânname içerik ekibine katılmak için ilk adımı atın.
                        </Typography>
                    </Box>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Ad Soyad"
                            name="full_name"
                            variant="outlined"
                            value={formData.full_name}
                            onChange={handleChange}
                            required
                            sx={darkInputStyles}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon sx={{ color: '#6b7280' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            label="E-Posta Adresi"
                            name="email"
                            type="email"
                            variant="outlined"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            sx={darkInputStyles}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon sx={{ color: '#6b7280' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Neden Mekanname'de Editör Olmak İstiyorsunuz?"
                            name="reason"
                            multiline
                            rows={4}
                            variant="outlined"
                            value={formData.reason}
                            onChange={handleChange}
                            required
                            placeholder="Deneyimlerinizden veya ilgi alanlarınızdan kısaca bahsedin..."
                            sx={darkInputStyles}
                        />

                        <Button 
                            type="submit" 
                            fullWidth 
                            variant="contained" 
                            size="large"
                            disabled={loading}
                            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                            sx={{
                                mt: 1,
                                py: 1.8,
                                borderRadius: 2,
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                textTransform: 'none',
                                background: 'linear-gradient(45deg, #f59e0b 30%, #fbbf24 90%)', // Amber Gradient
                                color: '#1f2937', // Koyu yazı rengi (okunabilirlik için)
                                boxShadow: '0 3px 5px 2px rgba(245, 158, 11, .3)',
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #d97706 30%, #f59e0b 90%)',
                                    boxShadow: '0 6px 10px 4px rgba(245, 158, 11, .3)',
                                },
                                '&:disabled': {
                                    background: '#374151',
                                    color: '#9ca3af',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {loading ? 'Gönderiliyor...' : 'Başvuruyu Gönder'}
                        </Button>
                    </form>
                </Paper>
            </Container>

            {/* Bildirim */}
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity} 
                    variant="filled"
                    sx={{ width: '100%', borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default EditorBasvuruFormu;